import type { Column } from '@tanstack/react-table';
import type { CSSProperties } from 'react';

/**
 * Sticky positioning for a pinned column's header/body cell, so the column stays
 * visible while the table scrolls horizontally. Returns `undefined` for an
 * unpinned column, leaving it in normal flow. The header layer sits above the
 * body layer so the pinned header wins at the top corners.
 */
export function pinnedCellStyle<T>(
  column: Column<T>,
  layer: 'header' | 'body',
): CSSProperties | undefined {
  const pinned = column.getIsPinned();
  if (!pinned) return undefined;
  if (pinned === 'right') {
    return {
      position: 'sticky',
      right: `${String(column.getAfter('right'))}px`,
      zIndex: layer === 'header' ? 20 : 10,
    };
  }
  return {
    position: 'sticky',
    // The MEASURED right edge of the frozen columns before this one (set by the table from
    // the rendered header cells), falling back to the size arithmetic. Sizes alone drift
    // from the rendered box — density padding, fixed-layout width distribution — and the
    // difference opened a strip between frozen columns that scrolling text showed through.
    left: `var(${pinnedLeftVar(column.getPinnedIndex())}, ${String(column.getStart('left'))}px)`,
    zIndex: layer === 'header' ? 20 : 10,
  };
}

/** CSS custom property holding the measured left offset of the *index*-th pinned column. */
export function pinnedLeftVar(index: number): string {
  return `--dt-pin-left-${String(index)}`;
}

/** The leftmost right-pinned column — it carries the divider on its left edge. */
export function isFirstRightPinned<T>(column: Column<T>): boolean {
  return column.getIsPinned() === 'right' && column.getIsFirstColumn('right');
}

/** The rightmost left-pinned column — the one that carries the divider that
 *  separates the frozen column from the scrolling ones. */
export function isLastLeftPinned<T>(column: Column<T>): boolean {
  return column.getIsPinned() === 'left' && column.getIsLastColumn('left');
}
